import React from 'react';
import { Users, Clock, EyeOff } from 'lucide-react';

const PAINS = [
  {
    Icon: Users,
    accent: 'red',
    title: 'Overstaffed shifts',
    desc: 'An extra body "just in case" — without seeing what it adds to your weekly labor bill.',
  },
  {
    Icon: Clock,
    accent: 'amber',
    title: 'Overtime leaks',
    desc: 'OT piles up invisibly across the week — by the time payroll runs, it\'s too late to fix.',
  },
  {
    Icon: EyeOff,
    accent: 'violet',
    title: 'No real-time visibility',
    desc: 'You can\'t see labor cost mid-week. Managing blind. Correcting after the damage is done.',
  },
];

const ACCENT_CLASSES = {
  red:    { bg: 'bg-red-500/10',    border: 'border-red-500/20',    icon: 'text-red-400',    bar: 'bg-red-500'    },
  amber:  { bg: 'bg-amber-500/10',  border: 'border-amber-500/20',  icon: 'text-amber-400',  bar: 'bg-amber-500'  },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/20', icon: 'text-violet-400', bar: 'bg-violet-500' },
};

export function ProblemSection() {
  return (
    <section
      className="py-14 md:py-28 bg-slate-950 px-4"
      aria-label="The problem"
      id="problem"
    >
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10 md:mb-18">
          <p className="text-orange-400 text-[11px] md:text-xs font-bold uppercase tracking-widest mb-3 md:mb-4">The real problem</p>
          <h2
            className="text-[26px] md:text-5xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Manrope' }}
          >
            Scheduling without knowing
            <br className="hidden sm:block" /> what it actually costs.
          </h2>
          <p className="mt-4 md:mt-6 text-slate-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed hidden md:block">
            Most businesses only discover labor problems after payroll — when it's already too late to correct.
          </p>
        </div>

        {/* Pain cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {PAINS.map(({ Icon, accent, title, desc }) => {
            const cls = ACCENT_CLASSES[accent];
            return (
              <div
                key={title}
                className="relative bg-slate-900 rounded-2xl p-6 md:p-8 border border-slate-800 overflow-hidden"
                data-testid={`pain-card-${accent}`}
              >
                {/* Top accent bar */}
                <div className={`absolute top-0 left-0 right-0 h-0.5 ${cls.bar}`} />

                <div className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center mb-3 md:mb-5 ${cls.bg} border ${cls.border}`}>
                  <Icon size={18} className={cls.icon} />
                </div>

                <h3 className="text-white font-bold text-lg md:text-xl mb-2 md:mb-3" style={{ fontFamily: 'Manrope' }}>
                  {title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
              </div>
            );
          })}
        </div>

        {/* Bridge line — hidden on mobile to reduce scroll */}
        <div className="hidden md:block mt-16 text-center">
          <p className="text-slate-500 text-sm uppercase tracking-widest font-semibold">
            There's a better way
          </p>
          <div className="mt-4 w-px h-12 bg-gradient-to-b from-slate-500 to-transparent mx-auto" />
        </div>

      </div>
    </section>
  );
}
